import { useState, useMemo, useCallback } from 'react';

export function useFilter(layerOrder, filterComparer) {
  const [stackOrder] = useState([...layerOrder]);
  // NOTE: this should probably be a ref so you can modify it multiple times in a single render
  const [filters, setFilters] = useState(
    Object.fromEntries(stackOrder.map((o) => [o, null]))
  );

  const setFilter = useCallback(
    (layer, filter) => {
      setFilters(
        Object.fromEntries(
          stackOrder.map((o) => [o, layer === o ? filter : filters[o]])
        )
      );
    },
    [setFilters, stackOrder, filters]
  );

  const activeFilter = useMemo(() => {
    const activeLayer = stackOrder.find((layer) => !!filters[layer], [filters]);
    return activeLayer !== undefined ? filters[activeLayer] : null;
  }, [stackOrder, filters]);

  const clearFilter = useCallback(
    (...layers) => {
      setFilters(
        Object.fromEntries(
          stackOrder.map((o) => [o, layers.includes(o) ? null : filters[o]])
        )
      );
    },
    [setFilters, stackOrder, filters]
  );

  const resetFilters = useCallback(() => {
    setFilters(Object.fromEntries(stackOrder.map((o) => [o, null])));
  }, [setFilters, stackOrder]);

  const toggleFilter = useCallback(
    (layer, filter) => {
      if (filterComparer(filters[layer], filter)) {
        clearFilter(layer);
      } else {
        setFilter(layer, filter);
      }
    },
    [setFilter, clearFilter, filters, filterComparer]
  );

  return {
    setFilter,
    clearFilter,
    resetFilters,
    toggleFilter,
    activeFilter,
    filterStack: filters,
  };
}
